import isNil from 'lodash/isNil';

interface VersionedRecord {
  id: string;
  _version?: number | null;
}

interface SoftDeletableRecord {
  _deleted?: boolean | null;
}

/** True for a record that was found and is not soft-deleted. */
export const isExistingRecord = <T extends SoftDeletableRecord>(record: T | null | undefined): record is T =>
  !isNil(record) && !(record._deleted ?? false);

export const pickLatestVersion = <T extends VersionedRecord>(current: T | undefined, fetched: T): T =>
  !isNil(current) && (current._version ?? 0) > (fetched._version ?? 0) ? current : fetched;

/**
 * Replaces the list with a full fetch, except for the rows the state already holds at a higher `_version`:
 * a subscription event or a write received while the fetch was running is more recent than the snapshot.
 * Given the ids the state held when the fetch started, it also keeps the rows added since, which the snapshot may
 * miss, and does not bring back the rows removed since.
 */
export const mergeFullyFetchedRecords = <T extends VersionedRecord>(
  currentRecords: T[] | null,
  fetchedRecords: T[],
  idsAtFetchStart?: Set<string>
): T[] => {
  const currentRecordsById = new Map((currentRecords ?? []).map((record) => [record.id, record]));
  const merged = fetchedRecords.reduce<T[]>((acc, fetchedRecord) => {
    const currentRecord = currentRecordsById.get(fetchedRecord.id);
    const isRemovedSinceFetchStart = isNil(currentRecord) && (idsAtFetchStart?.has(fetchedRecord.id) ?? false);
    if (!isRemovedSinceFetchStart) {
      acc.push(pickLatestVersion(currentRecord, fetchedRecord));
    }
    return acc;
  }, []);
  if (isNil(idsAtFetchStart)) {
    return merged;
  }
  const fetchedIds = new Set(fetchedRecords.map(({ id }) => id));
  const addedSinceFetchStart = (currentRecords ?? []).filter(
    ({ id }) => !fetchedIds.has(id) && !idsAtFetchStart.has(id)
  );
  return [...merged, ...addedSinceFetchStart];
};
